import { GoogleGenerativeAI } from '@google/generative-ai';
import { DamageType } from "../types";

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || "");

// Rate limit hatası kontrolü
const isRateLimitError = (error: any): boolean => {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorStatus = error?.status || error?.code || 0;

  return (
    errorStatus === 429 ||
    errorMessage.includes('rate limit') ||
    errorMessage.includes('quota') ||
    errorMessage.includes('resource exhausted') ||
    errorMessage.includes('too many requests')
  );
};

// Analiz sonucu tipi
export interface AnalysisResult {
  damageType: string;
  severity: number; // 1-5 arası
  confidence: number;
  description: string;
  suggestions: string;
}

// Fonksiyon: Base64 formatındaki görüntüyü alır ve Gemini API ile analiz eder
export const analyzeBladeDamage = async (
  imageBase64: string,
  annotation: any,
  turbineName: string,
  bladeName: string,
  surfaceType: string
): Promise<AnalysisResult> => {

  console.log('[AI] Starting analysis...');
  console.log('[AI] API Key exists:', !!process.env.REACT_APP_GEMINI_API_KEY);

  if (!process.env.REACT_APP_GEMINI_API_KEY) {
    throw new Error('API Key eksik. Vercel environment variables kontrol edin.');
  }

  const generationConfig = {
    temperature: 0.3,
    topK: 32,
    topP: 0.9,
    maxOutputTokens: 1024,
  };

  // Basit ve net prompt
  const prompt = `Rüzgar türbini kanat hasarı analizi yap. SADECE JSON döndür:
{
  "damageType": "Leading Edge Erosion" veya "Surface Contamination" veya "Structural Crack",
  "severity": 1-5 arası sayı,
  "confidence": 1-100 arası sayı,
  "description": "Kısa açıklama (Türkçe)",
  "suggestions": "Kısa öneri (Türkçe)"
}`;

  const imageData = imageBase64.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');

  const imagePart = {
    inlineData: {
      data: imageData,
      mimeType: 'image/jpeg',
    },
  };

  // Model fallback - API'den doğrulanan MEVCUT modeller
  const models = [
    'gemini-2.5-flash',          // En yeni ve hızlı
    'gemini-2.0-flash',          // Yüksek performans
    'gemini-flash-latest',       // Alias
    'gemini-pro-latest'          // Fallback
  ];

  for (const modelName of models) {
    try {
      console.log(`[AI] Trying model: ${modelName}`);
      const model = genAI.getGenerativeModel({
        model: modelName,
        generationConfig: {
          ...generationConfig,
          // JSON mode 2.0+ modellerde destekleniyor
          responseMimeType: "application/json",
        }
      });

      const result = await model.generateContent([
        prompt,
        imagePart
      ]);

      const response = result.response;
      const text = response.text();
      console.log(`[AI] Raw response:`, text);

      // JSON parse - Robust Clean
      let parsed;
      try {
        // Temizlik: Markdown code blocks, backticks ve "json" kelimesini temizle
        const cleanText = text
          .replace(/```json/gi, '') // ```json sil (case insensitive)
          .replace(/```/g, '')      // kalan ``` sil
          .trim();                  // boşlukları temizle

        parsed = JSON.parse(cleanText);
      } catch (e) {
        console.warn('[AI] Direct parse failed, trying regex extraction...');
        // Regex ile ilk { ve son } arasını bul
        const match = text.match(/\{[\s\S]*\}/);
        if (match) {
          try {
            parsed = JSON.parse(match[0]);
          } catch (e2) {
            console.error('[AI] Regex extraction also failed:', e2);
            throw new Error('Valid JSON could not be extracted from response.');
          }
        } else {
          throw new Error('No JSON object found in response.');
        }
      }

      console.log('[AI] Parsed successfully:', parsed);

      return {
        damageType: parsed.damageType || 'Surface Contamination',
        severity: typeof parsed.severity === 'number' ? parsed.severity : 3,
        confidence: typeof parsed.confidence === 'number' ? parsed.confidence : 50,
        description: parsed.description || 'Analiz tamamlandı.',
        suggestions: parsed.suggestions || 'İnceleme önerilir.',
      };
    } catch (error: any) {
      console.error(`[AI] Model ${modelName} failed:`, error);
      if (isRateLimitError(error)) continue;
      continue;
    }
  }

  console.error('[AI] All models failed');
  throw new Error('AI analizi başarısız. Console logları kontrol edin.');
};

// Fonksiyon: Konva stage'den belirli bir alanın base64 görüntüsünü alır
export const captureAnnotationArea = (
  stage: any,
  image: HTMLImageElement,
  annotation: { x: number; y: number; width: number; height: number },
  scale: number
): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas 2D context oluşturulamadı');
    }

    canvas.width = annotation.width;
    canvas.height = annotation.height;

    ctx.drawImage(
      image,
      annotation.x, annotation.y, annotation.width, annotation.height,
      0, 0, annotation.width, annotation.height
    );

    return canvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    console.error('Görüntü alınırken hata:', error);
    return '';
  }
};